import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import cx from 'classnames';
import { Field } from 'redux-form';

import { isTouchDevice } from '../../utilities/feature-tests';
import { getCountryCodeIndex } from '../../intl/utils';
import { getFormInputClasses, setErrorWidth } from '../../utilities/form-helpers';
import { getFormattedNumberNoCountryCode } from '../../utilities/helpers';

import Dropdown from './dropdown';
import FormInputCorrectMessage from './form-input-correct-message';
import FormInputErrorMessage from './form-input-error-message';
import FormInputLabel from './form-input-label';

const mapStateToProps = state => {
  return ({
    storefront: state.Manage.storefront,
    countryContent: state.Countries.countryContent,
  });
};

class FormInputPhone extends Component {
  static defaultProps = {
    isDisabled: false,
    isHidden: false,
    isRequired: false,
    shouldSetErrorWidth: false,
  }

  constructor(props) {
    super(props);

    /**
     * @type {Object} state
     * @prop {Number} errorWidth - max width of error message container
     * @prop {Object} hasBeenFocused - Has user focused on field
     * @prop {Object} selectedCountry - Country data for selected country
     */

    this.state = {
      errorWidth: null,
      hasBeenFocused: false,
      selectedCountry: {},
    };

    this.handleFocus = this.handleFocus.bind(this);
    this.handleDropdownChange = this.handleDropdownChange.bind(this);
  }

  componentWillMount() {
    const {
      countryContent,
      input: { value },
      storefront,
    } = this.props;

    const country = storefront ? storefront.split('-')[1] : 'MX';
    const defaultValue = value && value.isoCode ? value.isoCode : country;

    let countryCodeIndex = null;
    let defaultCountry = null;

    if (countryContent && countryContent.length) {
      countryCodeIndex = getCountryCodeIndex(defaultValue, countryContent);
      defaultCountry = countryContent[countryCodeIndex] || [];
    }

    this.setState({
      selectedCountry: defaultCountry,
    });
  }

  componentDidMount() {
    const { input: { onBlur, value } } = this.props;

    if (value && value.phoneNumber) {
      onBlur();
    }
  }

  /**
   * @param {Object} nextProps - updated props for component
   */
  componentWillReceiveProps(nextProps) {
    const {
      input: { onBlur },
    } = this.props;

    const { hasBeenFocused } = this.state;
    // Handle blur & format number if field is populated without
    // being focused e.g. on browser autofill

    if (nextProps.input.value && hasBeenFocused && nextProps.meta.valid === false && !nextProps.meta.pristine && !nextProps.meta.active) {
      this.setState({ hasBeenFocused: false }, () => {
        onBlur({
          isoCode: this.state.selectedCountry.value,
          phoneNumber: nextProps.input.value.phoneNumber,
        });
      });
    }
  }

  componentDidUpdate() {
    const { shouldSetErrorWidth } = this.props;
    const { errorWidth } = this.state;

    if (this.formInputErrorDOM && shouldSetErrorWidth) {
      const newErrorWidth = setErrorWidth(this.formInputErrorDOM.getBoundingClientRect().left);

      if (newErrorWidth !== errorWidth) {
        this.setState({
          errorWidth: newErrorWidth,
        });
      }
    }
  }

  render() {
    const {
      className,
      countryContent,
      input: { name, value },
      inputClassModifier,
      isDisabled,
      isHidden,
      isRequired,
      label,
      labelClassModifier,
      labelCopyID,
      maxLength,
      meta: {
        active,
        error,
        touched,
        valid,
      },
      numberPhone,
      tabIndex,
      showOptionalLabel,
      validate,
    } = this.props;

    const {
      errorWidth,
      selectedCountry,
    } = this.state;

    const isError = error && touched;
    const isValid = valid && value && value.phoneNumber;
    let inputDescribedBy = null;

    if (isError) {
      inputDescribedBy = `${ name }-error-text`;
    }

    const formFieldClasses = cx({
      'FormInput': true,
      'FormInputPhone': true,
      'is-disabled': isDisabled,
      'is-error': isError,
      'is-correct': isValid,
      'is-inFocus': active,
    }, className);

    return (
      <div className={ getFormInputClasses(inputClassModifier, isHidden) }>
        <div className={ formFieldClasses } >
          <FormInputLabel
            name={ 'name' }
            isRequired={ isRequired }
            label={ label }
            labelClassModifier={ labelClassModifier }
            labelCopyID={ labelCopyID }
            showOptionalLabel={ showOptionalLabel } />
          <div className='FormInputPhone-wrapper'>

            { !numberPhone &&
            <Field
              autocompleteName='tel tel-country-code'
              className='FormInputPhone-dropdown'
              defaultOption={ getCountryCodeIndex(selectedCountry.value, countryContent) }
              component={ Dropdown }
              name={ `${ name }.tel-country-code` }
              options={ this.getCountryDropdownOptions() }
              onSelect={ this.handleDropdownChange }
              tabIndex={ tabIndex } />
            }

            <div className={ `FormInputPhone-input ${ numberPhone ? 'FormInputPhone-input-fourNumber' : '' }` }>
              <input
                aria-describedby={ inputDescribedBy }
                aria-disabled={ isDisabled }
                aria-invalid={ isError }
                autoComplete={ isRequired ? 'tel tel-national' : null }
                className='FormInput-input FormInputPhone-inputField'
                id={ name }
                name={ name }
                onBlur={ this.handleBlur }
                onChange={ this.handleChange }
                onFocus={ this.handleFocus }
                tabIndex={ tabIndex }
                type={ isTouchDevice() ? 'number' : 'text' }
                validate={ validate }
                maxLength={ numberPhone ? maxLength : null }
                onKeyDown={ e => (isNaN(e.key) && e.key !== 'Backspace') && e.preventDefault() }
              />

              <div
                className='FormInputPhone-inputDisplay'
                onClick={ this.handleFocus }>
                { isValid ? getFormattedNumberNoCountryCode(value, selectedCountry) : value.phoneNumber }
              </div>
            </div>

          </div>
          { isError ?
            <FormInputErrorMessage
              name={ name }
              error={ error }
              errorWidth={ errorWidth }
              ref={ (c) => this.formInputErrorDOM = ReactDOM.findDOMNode(c) } /> : null }
          { isValid &&
            <FormInputCorrectMessage
              isLoginModal={ true }
              value={ value } />
          }
        </div>
      </div>
    );
  }

  /* Event handlers */

  /**
   * @param {Number} i - Selected country index
   */
  handleDropdownChange(i) {
    const {
      countryContent,
      input: { onBlur, value },
    } = this.props;

    const selectedCountry = countryContent[i];

    this.setState({
      selectedCountry: selectedCountry,
    });

    // Only save to reducer if user has already entered
    // a phone number (e.g. don't validate when only a
    // country selection has been made)
    if (value) {
      onBlur({
        phoneNumber: value ? value.phoneNumber : '',
        isoCode: selectedCountry.value,
        type: value.type,
      });
    }
  }

  /**
   * @param {SyntheticEvent} e
   */
  handleBlur = (e) => {
    const {
      input: { onBlur, value },
      phoneType,
    } = this.props;

    const {
      hasBeenFocused,
      selectedCountry,
    } = this.state;

    onBlur({
      phoneNumber: e ? e.target.value : value.phoneNumber,
      isoCode: selectedCountry.value,
      type: phoneType || undefined,
    });

    if (!hasBeenFocused) {
      this.setState({
        hasBeenFocused: true,
      });
    }
  }

  /**
   * @param {SyntheticEvent} e
   */
  handleChange = (e) => {
    const {
      input: {
        onChange,
      },
      phoneType,
    } = this.props;
    const { selectedCountry } = this.state;

    onChange({
      phoneNumber: e.target.value,
      isoCode: selectedCountry.value,
      type: phoneType || undefined,
    });
  }

  handleFocus() {
    const { input: { onFocus } } = this.props;
    const { hasBeenFocused } = this.state;
    onFocus();

    if (!hasBeenFocused) {
      this.setState({
        hasBeenFocused: true,
      });
    }
  }

  getCountryDropdownOptions() {
    const { countryContent } = this.props;

    return countryContent.map(country => {
      const displayName = `${ country.name } (+${ country.code })`;

      return {
        name: displayName,
        value: country.value,
      };
    });
  }

  getCountryOptionsList(optionObj) {
    return optionObj.map((country) => {
      return <option value={ country.value } key={ country.value }>{ country.name }</option>;
    });
  }
}

FormInputPhone = connect(mapStateToProps)(FormInputPhone);

export default FormInputPhone;